import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { AzButtonComponent } from '../az-button/az-button.component';
import { AzSelectorComponent } from '../az-selector/az-selector.component';
import { AzSearchbarComponent } from '../az-searchbar/az-searchbar.component';
import { SpinnerComponent } from '../spinner/spinner.component';
import { AzWarningBannerComponent } from '../az-warning-banner/az-warning-banner.component';
import { ScrollActivityDirective } from '../../directives/scroll-activity.directive';
import {
  ContactsApiResponse,
  RecipientContact,
  CUSTOM_DOCUMENT_OPTION_KEY,
} from '../../../features/document-repository/models/document-entry.model';
import { AzSelectorOption } from '../az-selector/az-selector.component';
import config from '../../../../config.js';

@Component({
  selector: 'app-az-document-update-modal',
  imports: [
    FormsModule,
    AzButtonComponent,
    AzSelectorComponent,
    AzSearchbarComponent,
    SpinnerComponent,
    AzWarningBannerComponent,
    ScrollActivityDirective,
  ],
  templateUrl: './az-document-update-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './az-document-update-modal.component.scss',
})
export class AzDocumentUpdateModalComponent implements OnChanges {
  @Input() isOpen: boolean = false;
  @Input() isUploading: boolean = false;
  @Input() documentTypes: Array<string | AzSelectorOption> = [];
  @Input() existingDocumentNames: string[] = [];
  @Input() supportsCustomDocumentName = false;
  @Input() allowedFileTypes: string[] = []; // e.g., ['xlsx', 'docx']
  @Input() showCommentBox: boolean = true; // Control visibility of comment box
  @Input() preselectedDocumentType: string = ''; // Pre-selected document type
  @Input() disableDocumentTypeSelector: boolean = false; // Disable the selector
  @Input() showNotificationStep: boolean = false;
  @Input() contacts: ContactsApiResponse | null = null;
  @Input() isLoadingContacts: boolean = false;
  @Input() contactsError: string = '';
  @Output() closed = new EventEmitter<void>();
  @Output() upload = new EventEmitter<{
    file: File | null;
    documentType: string;
    comments: string;
    isNewDocument?: boolean;
    newDocumentName?: string;
  }>();
  @Output() stepOneConfirmed = new EventEmitter<{
    file: File | null;
    documentType: string;
    comments: string;
  }>();
  @Output() contactsRequested = new EventEmitter<void>();
  @Output() notifyConfirmed = new EventEmitter<{
    replacementFile: File;
    documentType: string;
    adobePdf: File;
    selectedContacts: RecipientContact[];
  }>();

  // Step 1 state
  selectedFile: File | null = null;
  selectedDocumentType: string = '';
  selectedDocumentOptionKey = '';
  newDocumentName = '';
  comments: string = '';
  isDragging: boolean = false;
  errorMessage: string = '';

  // Steps 1–4: replacement file, contract, recipients, confirmation
  readonly currentStep = signal<1 | 2 | 3 | 4>(1);
  readonly step1Data = signal<{ file: File | null; documentType: string; comments: string } | null>(
    null,
  );
  readonly adobePdfFile = signal<File | null>(null);
  readonly adobePdfError = signal<string>('');
  readonly recipientContacts = signal<RecipientContact[]>([]);
  readonly selectedContactKeys = signal<Set<string>>(new Set());
  readonly contactSearchTerm = signal('');
  readonly showAffectedEntities = signal(false);

  readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
  readonly MAX_ADOBE_PDF_SIZE = 10 * 1024 * 1024; // 10MB in bytes

  ngOnChanges(changes: SimpleChanges): void {
    // Pre-select document type when modal opens or preselectedDocumentType changes
    if (changes['preselectedDocumentType'] && this.preselectedDocumentType) {
      this.selectedDocumentType = this.preselectedDocumentType;
      this.selectedDocumentOptionKey = this.findOptionKey(this.preselectedDocumentType);
    }

    // Re-apply preselection when modal opens
    if (changes['isOpen'] && this.isOpen && this.preselectedDocumentType) {
      this.selectedDocumentType = this.preselectedDocumentType;
      this.selectedDocumentOptionKey = this.findOptionKey(this.preselectedDocumentType);
    }

    // Auto-select when only one document type is available, if no preselection is provided
    if (
      changes['documentTypes'] &&
      !this.preselectedDocumentType &&
      this.documentTypes.length === 1 &&
      this.getOptionKey(this.documentTypes[0]) !== CUSTOM_DOCUMENT_OPTION_KEY
    ) {
      this.selectedDocumentType = this.getOptionValue(this.documentTypes[0]);
      this.selectedDocumentOptionKey = this.getOptionKey(this.documentTypes[0]);
    }

    // Auto-select when modal opens with single document type, if no preselection is provided
    if (
      changes['isOpen'] &&
      this.isOpen &&
      !this.preselectedDocumentType &&
      this.documentTypes.length === 1 &&
      this.getOptionKey(this.documentTypes[0]) !== CUSTOM_DOCUMENT_OPTION_KEY
    ) {
      this.selectedDocumentType = this.getOptionValue(this.documentTypes[0]);
      this.selectedDocumentOptionKey = this.getOptionKey(this.documentTypes[0]);
    }

    // Reset all state (including step 2) when modal is closed
    if (changes['isOpen'] && !this.isOpen && !changes['isOpen'].firstChange) {
      this.resetForm();
    }

    if (changes['contacts']) {
      const contacts = this.toRecipientContacts(this.contacts);
      this.recipientContacts.set(contacts);
      this.selectedContactKeys.set(new Set());
    }
  }

  get ALLOWED_EXTENSIONS(): string[] {
    if (this.allowedFileTypes.length > 0) {
      return this.allowedFileTypes.map((type) => `.${type.toLowerCase()}`);
    }
    return ['.docx', '.xlsx']; // Default fallback
  }

  get acceptedFormats(): string {
    return this.ALLOWED_EXTENSIONS.join(',');
  }

  get supportedFormatsText(): string {
    return this.ALLOWED_EXTENSIONS.join(', ');
  }

  get shouldShowDocumentTypeSelector(): boolean {
    return this.supportsCustomDocumentName || this.documentTypes.length > 1;
  }

  get isCustomDocumentSelected(): boolean {
    return this.selectedDocumentOptionKey === CUSTOM_DOCUMENT_OPTION_KEY;
  }

  get trimmedNewDocumentName(): string {
    return this.newDocumentName.trim();
  }

  get isNewDocumentNameValid(): boolean {
    const name = this.trimmedNewDocumentName.toLowerCase();
    return (
      !!name &&
      !this.existingDocumentNames.some((existing) => existing.trim().toLowerCase() === name)
    );
  }

  get newDocumentNameWarning(): string {
    return this.isNewDocumentNameValid
      ? ''
      : 'Please enter a unique file name. This name is empty or already in use.';
  }

  get confirmUploadDisabled(): boolean {
    return (
      !this.selectedFile ||
      !this.selectedDocumentType ||
      (this.isCustomDocumentSelected &&
        (!this.isNewDocumentNameValid || this.selectedFile.size <= 0)) ||
      this.isUploading
    );
  }

  getOptionLabel(option: string | AzSelectorOption): string {
    return typeof option === 'string' ? option : option.label;
  }

  private getOptionValue(option: string | AzSelectorOption): string {
    return typeof option === 'string' ? option : option.value;
  }

  private getOptionKey(option: string | AzSelectorOption): string {
    return typeof option === 'string' ? '' : (option.key ?? '');
  }

  private findOptionKey(value: string): string {
    const option = this.documentTypes.find((item) => this.getOptionValue(item) === value);
    return option ? this.getOptionKey(option) : '';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.validateAndSetFile(input.files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.validateAndSetFile(event.dataTransfer.files[0]);
    }
  }

  validateAndSetFile(file: File): void {
    this.errorMessage = '';

    // Validate file extension
    const fileName = file.name.toLowerCase();
    const hasValidExtension = this.ALLOWED_EXTENSIONS.some((ext) => fileName.endsWith(ext));

    if (!hasValidExtension) {
      this.errorMessage = `Invalid file format. Please upload ${this.supportedFormatsText} files only.`;
      this.selectedFile = null;
      return;
    }

    // Validate file size
    if (file.size > this.MAX_FILE_SIZE) {
      this.errorMessage = 'File size exceeds 10MB. Please upload a smaller file.';
      this.selectedFile = null;
      return;
    }

    // File is valid
    this.selectedFile = file;
  }

  removeFile(): void {
    this.selectedFile = null;
    this.errorMessage = '';
  }

  get selectedFileName(): string {
    return this.selectedFile ? this.selectedFile.name : '';
  }

  get selectedFileSize(): string {
    if (!this.selectedFile) return '';
    const sizeInMB = (this.selectedFile.size / (1024 * 1024)).toFixed(2);
    return `${sizeInMB} MB`;
  }

  onCancel(): void {
    this.resetForm();
    this.closed.emit();
  }

  onConfirm(): void {
    if (this.showNotificationStep) {
      // Two-step mode: store step 1 data and advance to step 2
      const data = {
        file: this.selectedFile,
        documentType: this.selectedDocumentType,
        comments: this.comments,
      };
      this.step1Data.set(data);
      this.currentStep.set(2);
      this.stepOneConfirmed.emit(data);
    } else {
      // Standard mode: emit upload immediately
      this.upload.emit({
        file: this.selectedFile,
        documentType: this.isCustomDocumentSelected ? 'Other' : this.selectedDocumentType,
        comments: this.comments,
        ...(this.isCustomDocumentSelected
          ? { isNewDocument: true, newDocumentName: this.trimmedNewDocumentName }
          : {}),
      });
    }
  }

  onDocumentTypeChange(type: string): void {
    this.selectedDocumentType = type;
    this.selectedDocumentOptionKey = this.findOptionKey(type);
  }

  onDocumentTypeOptionChange(option: string | AzSelectorOption): void {
    this.selectedDocumentType = this.getOptionValue(option);
    this.selectedDocumentOptionKey = this.getOptionKey(option);
  }

  // Step 2: Adobe PDF handling
  validateAndSetAdobePdf(file: File): void {
    this.adobePdfError.set('');
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.pdf')) {
      this.adobePdfError.set('Invalid file format. Please upload .pdf files only.');
      this.adobePdfFile.set(null);
      return;
    }
    if (file.size > this.MAX_ADOBE_PDF_SIZE) {
      this.adobePdfError.set('File size exceeds 10MB. Please upload a smaller file.');
      this.adobePdfFile.set(null);
      return;
    }
    this.adobePdfFile.set(file);
  }

  removeAdobePdf(): void {
    this.adobePdfFile.set(null);
    this.adobePdfError.set('');
  }

  onAdobePdfSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.validateAndSetAdobePdf(input.files[0]);
    }
  }

  onAdobePdfDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onAdobePdfDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onAdobePdfDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.validateAndSetAdobePdf(event.dataTransfer.files[0]);
    }
  }

  get adobePdfFileSize(): string {
    const file = this.adobePdfFile();
    if (!file) return '';
    const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
    return `${sizeInMB} MB`;
  }

  // Step 2: Navigation
  onBack(): void {
    this.currentStep.set(1);
  }

  // Step 3: Back to contract upload
  onStep3Back(): void {
    this.currentStep.set(2);
  }

  // Step 4: Back to recipient selection
  onStep4Back(): void {
    this.currentStep.set(3);
  }

  get confirmContractDisabled(): boolean {
    return !this.adobePdfFile() || !!this.adobePdfError();
  }

  // Step 2: Confirm → load contacts and advance to recipient selection
  onNotifyConfirm(): void {
    if (this.confirmContractDisabled) return;
    if (!this.step1Data()?.file) return;
    this.currentStep.set(3);
    this.contactsRequested.emit();
  }

  // Step 3: Confirm selection → advance to final file review
  onContactsConfirm(): void {
    if (this.selectedContacts.length === 0 || this.hasSelectedContactMissingRequiredInfo) return;
    this.currentStep.set(4);
  }

  // Step 4: Final confirm — emit the upload event
  onStep3Confirm(): void {
    const s1 = this.step1Data();
    const adobePdf = this.adobePdfFile();
    if (!s1?.file || !adobePdf || this.selectedContacts.length === 0) return;
    this.notifyConfirmed.emit({
      replacementFile: s1.file,
      documentType: s1.documentType,
      adobePdf,
      selectedContacts: this.selectedContacts,
    });
  }

  onContactSearchChanged(term: string): void {
    this.contactSearchTerm.set(term);
  }

  get filteredContacts(): RecipientContact[] {
    const term = this.contactSearchTerm().trim().toLocaleLowerCase();
    if (!term) return this.recipientContacts();
    return this.recipientContacts().filter((contact) =>
      [contact.contact_name, contact.email, this.getLegalEntityDisplayName(contact)].some((value) =>
        value?.toLocaleLowerCase().includes(term),
      ),
    );
  }

  getLegalEntityDisplayName(contact: RecipientContact): string {
    return contact.legal_entity_name || 'N/A (IGA Coordinator)';
  }

  get selectedContacts(): RecipientContact[] {
    const selectedKeys = this.selectedContactKeys();
    return this.recipientContacts().filter((contact) => selectedKeys.has(contact.key));
  }

  get hasSelectedContactMissingRequiredInfo(): boolean {
    return this.selectedContacts.some(
      (contact) => !contact.contact_name?.trim() || !contact.email?.trim(),
    );
  }

  get incompleteSelectedContacts(): RecipientContact[] {
    return this.selectedContacts.filter(
      (contact) => !contact.contact_name?.trim() || !contact.email?.trim(),
    );
  }

  get incompleteContactsCountLabel(): string {
    return String(this.incompleteSelectedContacts.length);
  }

  // BCR-253 follow-up: submissions are processed in batches of up to 99
  // signatories in prod, or 25 in every other environment. Public so the
  // template can interpolate the limit instead of hard-coding it, avoiding
  // drift.
  readonly maxContactsPerBatch = config.environment_name === 'prod' ? 99 : 25;

  // A contact can appear in multiple selected rows (e.g. the same person is
  // the contact for several legal entities), so the unique-people count is
  // derived by deduping on email, matching the convention already used in
  // document-repository-contacts.component.ts's applyContactGrouping().
  // Blank/unknown emails are never merged together since they can't be
  // confirmed to be the same person.
  get uniqueSelectedContactsCount(): number {
    const seenEmails = new Set<string>();
    let uniqueCount = 0;
    for (const contact of this.selectedContacts) {
      const email = contact.email?.trim().toLowerCase();
      if (!email) {
        uniqueCount++;
        continue;
      }
      if (!seenEmails.has(email)) {
        seenEmails.add(email);
        uniqueCount++;
      }
    }
    return uniqueCount;
  }

  get selectedGroupsCount(): number {
    return Math.ceil(this.uniqueSelectedContactsCount / this.maxContactsPerBatch);
  }

  // Pluralization-safe labels for the counts above, shared between the Step 3
  // selection footer and the Step 4 confirmation summary.
  get legalEntitiesSelectedLabel(): string {
    const count = this.selectedContacts.length;
    return `${count} legal ${count === 1 ? 'entity' : 'entities'} selected`;
  }

  get uniqueContactsCountLabel(): string {
    const count = this.uniqueSelectedContactsCount;
    return `${count} ${count === 1 ? 'contact' : 'contacts'}`;
  }

  get selectedGroupsCountLabel(): string {
    const count = this.selectedGroupsCount;
    return `${count} ${count === 1 ? 'group' : 'groups'}`;
  }

  get hasSelectedContactsExceedingBatchLimit(): boolean {
    return this.uniqueSelectedContactsCount > this.maxContactsPerBatch;
  }

  showAffectedEntitiesPanel(): void {
    this.showAffectedEntities.set(true);
  }

  hideAffectedEntitiesPanel(): void {
    this.showAffectedEntities.set(false);
  }

  get areAllVisibleContactsSelected(): boolean {
    const visibleContacts = this.filteredContacts;
    return (
      visibleContacts.length > 0 &&
      visibleContacts.every((contact) => this.selectedContactKeys().has(contact.key))
    );
  }

  toggleContact(contact: RecipientContact): void {
    const selectedKeys = new Set(this.selectedContactKeys());
    if (selectedKeys.has(contact.key)) {
      selectedKeys.delete(contact.key);
    } else {
      selectedKeys.add(contact.key);
    }
    this.selectedContactKeys.set(selectedKeys);
  }

  toggleAllVisibleContacts(): void {
    const selectedKeys = new Set(this.selectedContactKeys());
    const visibleContacts = this.filteredContacts;
    const shouldSelect = !this.areAllVisibleContactsSelected;
    visibleContacts.forEach((contact) => {
      if (shouldSelect) {
        selectedKeys.add(contact.key);
      } else {
        selectedKeys.delete(contact.key);
      }
    });
    this.selectedContactKeys.set(selectedKeys);
  }

  downloadFile(file: File | null | undefined): void {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    a.click();
    URL.revokeObjectURL(url);
  }

  resetForm(): void {
    this.selectedFile = null;
    this.selectedDocumentType = '';
    this.selectedDocumentOptionKey = '';
    this.newDocumentName = '';
    this.comments = '';
    this.isDragging = false;
    this.errorMessage = '';
    // Reset step 2 / 3 state
    this.currentStep.set(1);
    this.step1Data.set(null);
    this.adobePdfFile.set(null);
    this.adobePdfError.set('');
    this.recipientContacts.set([]);
    this.selectedContactKeys.set(new Set());
    this.contactSearchTerm.set('');
    this.showAffectedEntities.set(false);
  }

  private toRecipientContacts(response: ContactsApiResponse | null): RecipientContact[] {
    if (!response) return [];
    const contacts: RecipientContact[] = [
      ...response.contacts.iga_coordinators.map((contact, index) => ({
        key: `iga-${index}`,
        contact_name: contact.contact_name,
        email: contact.email,
        legal_entity_name: null,
        legal_entity_id: null,
      })),
      ...response.contacts.legal_entity_contacts.map((contact, index) => ({
        key: `legal-entity-${index}`,
        contact_name: contact.contact_name,
        email: contact.email,
        legal_entity_name: contact.legal_entity_name,
        legal_entity_id: contact.legal_entity_id ?? null,
      })),
    ];

    return this.isAlphabeticallySorted(contacts)
      ? contacts
      : [...contacts].sort(this.compareContacts);
  }

  private isAlphabeticallySorted(contacts: RecipientContact[]): boolean {
    return contacts.every(
      (contact, index) => index === 0 || this.compareContacts(contacts[index - 1], contact) <= 0,
    );
  }

  private compareContacts = (a: RecipientContact, b: RecipientContact): number => {
    const compare = (first: string | null, second: string | null) =>
      (first ?? '').localeCompare(second ?? '', undefined, { sensitivity: 'base' });
    return (
      compare(a.contact_name, b.contact_name) ||
      compare(a.legal_entity_name, b.legal_entity_name) ||
      compare(a.email, b.email)
    );
  };
}
